// "This isn't part of your role here" — the page a dispatcher or technician
// gets instead of a screen their role cannot read (PRD 89 §4.9).
//
// ONE SENTENCE FOR THE PAGE, NOT A TOAST PER REQUEST. A closed page fires
// several requests and every one comes back 403 `role_forbidden`; the shell
// (LiveBusinessShell) draws this in place of the page once, for the page that
// asked, and the person moves on through the menu, which already shows only
// what the role reaches. The api is the security here; this is the courtesy.

import React from 'react';
import styled from 'styled-components';
import { Lock } from 'lucide-react';
import { GBButton, GBEmptyState } from '@greenbackapp/ui';
import { BusinessRole } from '../../../types/businessApi';
import { ROLE_LABELS } from '../settings/roleCopy';
import { APPOINTMENTS_OVERVIEW_HOME_PATH, MY_DAY_PATH, POS_REGISTER_PATH, PROVIDER_HOME_PATH } from '../fieldRoleLanding';

/** The PRD's sentence, and the api's own (RestrictedRoleGate). */
export const ROLE_FORBIDDEN_TITLE = "This isn't part of your role here";

/**
 * A page each restricted role can always open, for the notice's one button.
 *
 * Customers for a dispatcher: the one core list the gate opens to that role
 * whether or not field service is set up. My profile for a technician (PRD 89
 * Wave 3) — their own page, and their landing until PRD 95's My day.
 */
const ROLE_HOMES: Partial<Record<BusinessRole, { path: string; label: string }>> = {
  dispatcher: { path: '/business/customers', label: 'Open Customers' },
  technician: { path: MY_DAY_PATH, label: 'Open My day' },
  register: { path: POS_REGISTER_PATH, label: 'Open the register' },
  // PRD 121 §4.10: the front desk's Overview until PRD 122's calendar; a
  // provider's own page (Wave 2) until PRD 122's day.
  front_desk: { path: APPOINTMENTS_OVERVIEW_HOME_PATH, label: 'Open Appointments' },
  provider: { path: PROVIDER_HOME_PATH, label: 'Open My profile' },
};

export const restrictedRoleHome = (
  role: string | null | undefined
): { path: string; label: string } | null =>
  (role ? ROLE_HOMES[role as BusinessRole] : undefined) ?? null;

// Centred in the page area, with the shell's own gutters: 32px on a wide
// screen, 16px on a phone.
const Page = styled.div`
  display: flex;
  justify-content: center;
  padding: 48px 32px;

  @media (max-width: 767px) {
    padding: 32px 16px;
  }
`;

export interface RoleForbiddenNoticeProps {
  role: string | null | undefined;
  businessName: string | null | undefined;
  /** Where the person is now, so the button never offers the page they are on. */
  currentPath: string;
  onNavigate: (path: string) => void;
}

export const RoleForbiddenNotice: React.FC<RoleForbiddenNoticeProps> = ({
  role,
  businessName,
  currentPath,
  onNavigate,
}) => {
  const label = role ? ROLE_LABELS[role as BusinessRole] : undefined;
  const where = businessName ? ` in ${businessName}` : '';
  const description = label
    ? `Your role${where} is ${label}, which doesn't include this page. Ask an owner if you need it.`
    : `Your role${where} doesn't include this page. Ask an owner if you need it.`;
  const home = restrictedRoleHome(role);
  const target = home !== null && home.path !== currentPath ? home : null;

  return (
    <Page>
      <GBEmptyState
        testID="role-forbidden"
        icon={<Lock size={28} />}
        title={ROLE_FORBIDDEN_TITLE}
        description={description}
        action={
          target ? (
            <GBButton variant="primary" size="md" onPress={() => onNavigate(target.path)}>
              {target.label}
            </GBButton>
          ) : undefined
        }
      />
    </Page>
  );
};

export default RoleForbiddenNotice;
